import React, { useState, useCallback, useEffect } from "react";
import { Button, Chip, Dialog, IconButton, Tabs } from "@material-tailwind/react";
import { toast } from "react-toastify";
import { Delete, Edit, Eye, PackagePlus, Plus, Trash2 } from "lucide-react";
import { DeleteButton, EditButton, PrimaryButton } from "../../components/Buttons";
import usePrivilege from "../../hooks/usePrivilege";
import { useUser } from "../../context/UserContext";
import { fetchAssignedConfigs, fetchPropertyConfigurations } from "../../services/projects";
import axiosInstance from "../../services/api/axios";
import ConfigModal from "./ConfigModal";
import CreateConfigModal from "./CreateConfigModal";
import CreateBuildingModal from "./CreateBuildingModal";
import AssignConfigToBuildingModal from "./AssignConfigToBuildingModal";
import ConfirmationModal from "../ConfirmationModal";

function BuildingsAndConfigs({ projectId, projectPhases, phaseId }) {
  const { userInfo } = useUser();
  const { hasPrivilege } = usePrivilege();
  
  // States for configurations
  const [projectConfigurations, setProjectConfigurations] = useState([]);
  const [editConfig, setEditConfig] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isConfigDetailsOpen, setIsConfigDetailsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshConfigurations, setRefreshConfigurations] = useState(false);
  
  // States for buildings
  const [buildings, setBuildings] = useState([]);
  const [refreshBuildings, setRefreshBuildings] = useState(false);
  const [isCreateBuildingModalOpen, setIsCreateBuildingModalOpen] = useState(false);
  const [selectedBuildingForEdit, setSelectedBuildingForEdit] = useState(null);
  const [selectedBuildingForAssignConfig, setSelectedBuildingForAssignConfig] = useState(null);
  const [isAssignConfigModalOpen, setIsAssignConfigModalOpen] = useState(false);
  
  // States for delete confirmations
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [isBuildingDeleteConfirmationOpen, setIsBuildingDeleteConfirmationOpen] = useState(false);
  const [buildingToDeleteId, setBuildingToDeleteId] = useState(null);
  
  // Active tab state
  const [activeInnerTab, setActiveInnerTab] = useState("buildings");

  // Fetch buildings data
  const fetchBuildingsData = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/projects/buildings", {
        params: {
          projectPhaseId: phaseId,
          customerId: projectPhases?.[0]?.project?.customerId,
        },
      });
      
      // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
      let buildingsArray = [];
      if (response?.items && Array.isArray(response.items)) {
        buildingsArray = response.items;
      } else if (Array.isArray(response)) {
        buildingsArray = response;
      }
      
      const buildingsWithConfigs = await Promise.all(
        buildingsArray.map(async (building) => {
          try {
            const assignedConfigsResponse = await fetchAssignedConfigs(projectId, building.buildingId, phaseId);
            // Handle paginated response for assigned configs
            let configsArray = [];
            if (assignedConfigsResponse?.items && Array.isArray(assignedConfigsResponse.items)) {
              configsArray = assignedConfigsResponse.items;
            } else if (Array.isArray(assignedConfigsResponse)) {
              configsArray = assignedConfigsResponse;
            }
            return {
              ...building,
              assignedConfigs: configsArray,
            };
          } catch (configError) {
            console.error(`Error fetching configs for building ${building.buildingId}:`, configError);
            return {
              ...building,
              assignedConfigs: [],
            };
          }
        })
      );
      setBuildings(buildingsWithConfigs);
    } catch (err) {
      console.error("Error fetching buildings data:", err);
      setBuildings([]);
    }
  }, [projectId, phaseId, projectPhases]);

  // Fetch configurations
  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        const response = await fetchPropertyConfigurations({
          projectId: projectId,
          customerId: userInfo?.loginIdentifierDetails?.customer?.customerId,
          projectPhaseId: phaseId,
        });
        
        // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
        let configurations = [];
        if (response?.items && Array.isArray(response.items)) {
          configurations = response.items;
        } else if (Array.isArray(response)) {
          configurations = response;
        }
        
        setProjectConfigurations(configurations);
        if (configurations.length > 0) {
          setSelectedConfig(configurations[0]);
          setEditConfig({ ...configurations[0] });
        }
      } catch (err) {
        console.error("Error fetching configurations:", err);
        setProjectConfigurations([]);
      }
    };

    if (projectId) {
      fetchConfigurations();
    }
  }, [projectId, userInfo, refreshConfigurations, phaseId]);

  // Fetch buildings when project changes
  useEffect(() => {
    if (projectId) {
      fetchBuildingsData();
    }
  }, [projectId, refreshBuildings, fetchBuildingsData]);

  // Configuration handlers
  const handleEditConfigClick = (selectedConfig) => {
    setIsModalOpen(true);
    setEditConfig(selectedConfig);
  };

  const handleConfigCreated = (newConfig) => {
    setRefreshConfigurations((prev) => !prev);
    setIsModalOpen(false);
    setEditConfig(null);
  };

  const handleAddConfig = () => {
    setIsModalOpen(true);
    setEditConfig(null);
  };

  const handleConfigDetailsOpen = (config) => {
    setSelectedConfig(config);
    setIsConfigDetailsOpen(true);
  };

  const handleConfigDetailsClose = () => {
    setIsConfigDetailsOpen(false);
    setSelectedConfig(null);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditConfig(null);
  };

  // Configuration delete handlers
  const handleDeleteConfirmation = async () => {
    try {
      if (!configToDelete) {
        console.error("No config selected for deletion.");
        return;
      }

      await axiosInstance.delete(`/property/config`, {
        params: {
          property_config_id: configToDelete.propertyConfigId
        }
      });

      setProjectConfigurations(projectConfigurations.filter(config => config.propertyConfigId !== configToDelete.propertyConfigId));
      toast.success('Configuration deleted successfully!');
    } catch (error) {
      console.error("Error deleting config:", error);
      toast.error('Failed to delete configuration.');
    } finally {
      closeDeleteConfirmation();
    }
  };

  const openDeleteConfirmation = (config) => {
    setConfigToDelete(config);
    setIsDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setIsDeleteConfirmationOpen(false);
    setConfigToDelete(null);
  };

  // Building handlers
  const handleBuildingCreated = useCallback(() => {
    setRefreshBuildings((prev) => !prev);
  }, []);

  const handleOpenCreateBuildingModal = () => {
    setSelectedBuildingForEdit(null);
    setIsCreateBuildingModalOpen(true);
  };

  const handleEditBuildingClick = (building) => {
    setSelectedBuildingForEdit(building);
    setIsCreateBuildingModalOpen(true);
  };

  const handleCloseCreateBuildingModal = () => {
    setIsCreateBuildingModalOpen(false);
    setSelectedBuildingForEdit(null);
  };

  const handleOpenAssignConfigModal = (building) => {
    setSelectedBuildingForAssignConfig(building);
    setIsAssignConfigModalOpen(true);
  };

  const handleCloseAssignConfigModal = () => {
    setIsAssignConfigModalOpen(false);
    setSelectedBuildingForAssignConfig(null);
  };

  // Building delete handlers
  const handleDeleteBuildingConfirmation = async () => {
    try {
      if (!buildingToDeleteId) {
        console.error("No building selected for deletion.");
        return;
      }

      await axiosInstance.delete(`/projects/buildings`, {
        params: { building_id: buildingToDeleteId }
      });
      toast.success("Building deleted successfully!");
      setBuildings(buildings.filter(building => building.buildingId !== buildingToDeleteId));
    } catch (error) {
      console.error("Error deleting building:", error);
      toast.error("Failed to delete building.");
    } finally {
      handleCloseBuildingDeleteConfirmation();
    }
  };

  const handleOpenBuildingDeleteConfirmation = (buildingId) => {
    setBuildingToDeleteId(buildingId);
    setIsBuildingDeleteConfirmationOpen(true);
  };

  const handleCloseBuildingDeleteConfirmation = () => {
    setIsBuildingDeleteConfirmationOpen(false);
    setBuildingToDeleteId(null);
  };

  const tabData = [
    {
      label: "Buildings",
      value: "buildings",
      component: (
        <div className=" p-4 lg:p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-xl font-bold">Buildings</h1>
            {hasPrivilege("canEdit") && (
              <PrimaryButton
                onClick={handleOpenCreateBuildingModal}
                icon={<Plus className="mr-2 size-4" />}
                left
              >
                Add Building
              </PrimaryButton>
            )}
          </div>
          <div className=" overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-20" style={{ height: '500px' }}>
            {buildings.length > 0 ? (
              buildings.map((building) => (
                <div
                  key={building.buildingId}
                  className="border rounded-lg p-4 shadow-sm bg-white mb-4"
                >
                  <div className="flex justify-between items-start gap-4">
                    {/* Building Details */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{building.buildingName}</h3>
                        {building.constructionLinkedPlan?.name && (
                          <span className="font-medium text-md text-gray-500">({building.constructionLinkedPlan.name})</span>
                        )}
                      </div>
                    
                      {/* Assigned Configurations */}
                      {building.assignedConfigs && building.assignedConfigs.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {building.assignedConfigs.map((config) => (
                            <span
                              key={config.propertyConfigId}
                              className="bg-gray-100 text-gray-800 text-sm px-2 py-1 rounded"
                            >
                              {config.propertyConfig.configName}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">
                          No configurations assigned.{" "}
                          {hasPrivilege("canEdit") && (
                            <button
                              type="button"
                              className="text-sm text-blue-600 underline hover:text-blue-800 focus:outline-none"
                              onClick={() => handleOpenAssignConfigModal(building)}
                            >
                              Assign now
                            </button>
                          )}
                        </span>
                      )}

                      {/* Floors Info */}
                      <p className="text-sm text-gray-500 mt-1">{building.numberOfFloors} Floors</p>
                    </div>
                  
                    {/* Action Buttons (Only if User Has Permission) */}
                    {hasPrivilege("canEdit") && (
                      <div className="flex gap-2">
                        <EditButton onClick={() => handleEditBuildingClick(building)} />
                        <DeleteButton onClick={() => handleOpenBuildingDeleteConfirmation(building.buildingId)} />
                        <Button
                          size="sm"
                          variant="outline"
                          color="black"
                          onClick={() => handleOpenAssignConfigModal(building)}
                        >
                          <PackagePlus size={20} />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">No buildings added yet.</p>
            )}
          </div>
          <AssignConfigToBuildingModal
            isOpen={isAssignConfigModalOpen}
            onClose={handleCloseAssignConfigModal}
            projectId={projectId}
            building={selectedBuildingForAssignConfig}
            configurations={projectConfigurations}
            onConfigurationAssigned={fetchBuildingsData}
          />
        </div>
      ),
    },
    {
      label: "Configurations",
      value: "configurations",
      component: (
        <div className=" p-4 lg:p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-xl font-bold">Configurations</h1>
            <div className="ml-1 self-end">
              {hasPrivilege("canEdit") && (
                <PrimaryButton
                  onClick={handleAddConfig}
                  icon={<Plus className="mr-2 size-4" />}
                  left
                >
                  Add Config
                </PrimaryButton>
              )}
            </div>
          </div>
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr>
                <th className="border p-2">Config Name</th>
                <th className="border p-2">Size</th>
                <th className="border p-2">Properties</th>
                <th className="border p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {projectConfigurations.length > 0 ? (
                projectConfigurations.map((config) => (
                  <tr key={config.propertyConfigId}>
                    <td className="border p-2">{config.configName}</td>
                    <td className="border p-2">{config.sizeBhk}</td>
                    <td className="border p-2">
                      {config.propertiesId ? config.propertiesId.length : 0}
                    </td>
                    <td className="border p-2">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          color="black"
                          onClick={() => handleConfigDetailsOpen(config)}
                          className="p-2"
                        >
                          <Eye size={17.5} />
                        </Button>
                        {hasPrivilege("canEdit") && (
                          <EditButton onClick={() => handleEditConfigClick(config)} />
                        )}
                        {hasPrivilege("canEdit") && (
                          <DeleteButton onClick={() => openDeleteConfirmation(config)} className="p-2" />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="border p-2 text-center">
                    No configurations available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="w-full space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
        <div className="hidden md:block">
          {/* <Tabs> stays as the layout container (it contributes the surrounding
              styling). We removed its `value` + `onChange` props because in
              Material-Tailwind v3 the wrapper takes ownership of the active
              tab via its own context and fires `onChange` with an event-like
              object that doesn't match our string state — leaving
              activeInnerTab undefined and rendering the "Select a tab" empty
              state. The inner custom divs below already implement tab
              selection via setActiveInnerTab, so the wrapper just needs to
              stay out of the way. */}
          <Tabs className="flex flex-col md:flex-row md:space-x-1 md:space-y-0 space-y-2 h-full">
            <div className="flex flex-wrap justify-center md:flex-nowrap items-center space-x-0 md:space-x-4 border-b border-slate-300 w-full h-full">
              {tabData.map(({ label, value }) => (
                <div
                  key={value}
                  onClick={() => setActiveInnerTab(value)}
                  className={`cursor-pointer py-2 px-8 w-1/2 md:w-auto text-center md:text-left ${activeInnerTab === value
                    ? "text-slate-900 border-b-2 border-flamingo-500 font-semibold"
                    : "text-slate-600"
                    }`}
                >
                  {label}
                </div>
              ))}
            </div>
            <div>
              {tabData.find((item) => item.value === activeInnerTab)?.component || (
                <p>Select a tab to view details.</p>
              )}
            </div>
          </Tabs>
        </div>
      </div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        onConfirm={handleDeleteConfirmation}
        onCancel={closeDeleteConfirmation}
        message={`Are you sure you want to delete configuration "${configToDelete?.configName}"?`}
      />
      
      <ConfirmationModal
        isOpen={isBuildingDeleteConfirmationOpen}
        onConfirm={handleDeleteBuildingConfirmation}
        onCancel={handleCloseBuildingDeleteConfirmation}
        message="Are you sure you want to delete this building? This action cannot be undone."
      />

      <CreateConfigModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        projectId={projectId}
        phaseId={phaseId}
        onCreate={handleConfigCreated}
        editConfigData={editConfig}
      />
      
      {selectedConfig && (
        <ConfigModal
          isOpen={isConfigDetailsOpen}
          selectedConfig={selectedConfig}
          onRequestClose={handleConfigDetailsClose}
        />
      )}
      
      <CreateBuildingModal
        isOpen={isCreateBuildingModalOpen}
        onClose={handleCloseCreateBuildingModal}
        projectId={projectId}
        phaseId={phaseId}
        onCreate={handleBuildingCreated}
        editBuildingData={selectedBuildingForEdit}
      />
    </>
  );
}

export default BuildingsAndConfigs;
